import React, { useEffect, useState } from 'react';
import {
  Box, Chip, Button, Alert, CircularProgress, Paper, TableContainer, Table, TableHead,
  TableBody, TableRow, TableCell, Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, Divider, Typography,
} from '@mui/material';
import { Edit, Save } from '@mui/icons-material';
import api from '../../services/api';

interface Carrier {
  id: string; name: string; host: string; status: string;
  owner: string; is_provider_carrier: boolean;
  billing_email?: string; billing_contact_name?: string; billing_phone?: string;
  technical_contact_name?: string; technical_contact_email?: string;
  rate_change_email?: string; cc_email?: string;
}

export const CarrierContactsPanel: React.FC = () => {
  const [rows, setRows] = useState<Carrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [edit, setEdit] = useState<Carrier | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/billing-contacts/carriers')
      .then(({ data }) => setRows(data || []))
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const set = (k: keyof Carrier, v: any) => setEdit((e) => (e ? { ...e, [k]: v } : e));

  const save = async () => {
    if (!edit) return;
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.put(`/billing-contacts/carriers/${edit.id}`, {
        billing_email: edit.billing_email, billing_contact_name: edit.billing_contact_name, billing_phone: edit.billing_phone,
        technical_contact_name: edit.technical_contact_name, technical_contact_email: edit.technical_contact_email,
        rate_change_email: edit.rate_change_email, cc_email: edit.cc_email,
      });
      setSuccess(`Saved contacts for ${edit.name}.`);
      setEdit(null); load();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Carrier / partner contacts. <b>Partner</b> = provider/termination carrier (BT, NGN…); the rest are customer ingress trunks.
      </Typography>

      <Paper variant="outlined">
        <TableContainer sx={{ maxHeight: '62vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Carrier</TableCell><TableCell>Type</TableCell><TableCell>Host</TableCell>
                <TableCell>Billing Email</TableCell><TableCell>Technical (NOC) Email</TableCell>
                <TableCell>Rate-change Email</TableCell><TableCell>CC Email</TableCell><TableCell align="center">Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell><b>{r.name}</b><div style={{ fontSize: 12, color: '#888' }}>{r.owner}</div></TableCell>
                  <TableCell><Chip size="small" color={r.is_provider_carrier ? 'info' : 'default'}
                    label={r.is_provider_carrier ? 'Partner' : 'Customer trunk'} /></TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{r.host}</TableCell>
                  <TableCell>{r.billing_email || '—'}</TableCell>
                  <TableCell>{r.technical_contact_email || '—'}</TableCell>
                  <TableCell>{r.rate_change_email || '—'}</TableCell>
                  <TableCell>{r.cc_email || '—'}</TableCell>
                  <TableCell align="center"><Button size="small" startIcon={<Edit />} onClick={() => setEdit({ ...r })}>Edit</Button></TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={8}><i>No carriers.</i></TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!edit} onClose={() => setEdit(null)} maxWidth="md" fullWidth>
        {edit && (<>
          <DialogTitle>Carrier contacts — {edit.name}</DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Billing contact</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Billing Email" value={edit.billing_email || ''} onChange={(e) => set('billing_email', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Billing Contact Name" value={edit.billing_contact_name || ''} onChange={(e) => set('billing_contact_name', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Billing Phone" value={edit.billing_phone || ''} onChange={(e) => set('billing_phone', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="CC (Carbon Copy) Email" value={edit.cc_email || ''} onChange={(e) => set('cc_email', e.target.value)} /></Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Technical &amp; notifications</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Technical / NOC Contact Name" value={edit.technical_contact_name || ''} onChange={(e) => set('technical_contact_name', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Technical / NOC Email" value={edit.technical_contact_email || ''} onChange={(e) => set('technical_contact_email', e.target.value)} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="Rate Change Notification Email" value={edit.rate_change_email || ''} onChange={(e) => set('rate_change_email', e.target.value)} helperText="Receives rate-update alerts" /></Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEdit(null)}>Cancel</Button>
            <Button variant="contained" startIcon={<Save />} disabled={saving} onClick={save}>Save</Button>
          </DialogActions>
        </>)}
      </Dialog>
    </Box>
  );
};
